import { candidateSeatKey, type HeightBand, type SeatCandidate } from '@anjabom/core';

export interface RemoteEvidenceRow {
  evidence: {
    tierCounts: { exact: number; nearby: number; section: number };
    evidence?: Array<{
      tier?: 'exact' | 'nearby' | 'section';
      contributorRef?: string | null;
      heightBand?: HeightBand | null;
      dimensions?: Partial<Record<'view' | 'block' | 'sound' | 'knee' | 'seat' | 'access', number | null>>;
    }>;
    otherConfiguration?: { count?: number };
    priceSampleCount?: number;
    priceDistribution?: { n: number; median: number; q1: number; q3: number } | null;
    priceGrade?: string | null;
    priceDistributionReason?: 'available' | 'no_recent_prices' | 'insufficient_sample' | 'seat_grade_unknown' | 'mixed_seat_grade';
  };
}

export interface SeatEvidence {
  tierCounts: { exact: number; nearby: number; section: number };
  evidence: Array<Record<string, unknown>>;
  otherConfiguration: { count: number };
  priceDistribution: { n: number; median: number; q1: number; q3: number } | null;
}

export class CompareEvidenceError extends Error {
  constructor(message: string, readonly code?: string) {
    super(message);
    this.name = 'CompareEvidenceError';
  }
}

interface LoadCompareEvidenceOptions {
  candidates: readonly SeatCandidate[];
  sameContext: boolean;
  turnstileToken: string | null;
  signal: AbortSignal;
  fetcher?: typeof fetch;
}

export async function loadCompareEvidence({
  candidates,
  sameContext,
  turnstileToken,
  signal,
  fetcher = fetch,
}: LoadCompareEvidenceOptions): Promise<RemoteEvidenceRow[]> {
  if (sameContext) {
    const first = candidates[0];
    if (!first) throw new CompareEvidenceError('비교할 좌석이 없어요.');

    const headers: Record<string, string> = { 'content-type': 'application/json' };
    if (turnstileToken) headers['cf-turnstile-response'] = turnstileToken;
    const response = await fetcher('/api/compare', {
      method: 'POST',
      headers,
      signal,
      body: JSON.stringify({
        venueId: first.venueId,
        layoutVersionId: first.layoutVersionId,
        instanceId: first.instanceId,
        candidates: candidates.map(({ sectionCode, rowLabel, seatNumber, expectedPrice }) => ({
          sectionCode,
          rowLabel,
          seatNumber,
          expectedPrice,
        })),
      }),
    });
    const value = await readJson(response);
    if (!response.ok) {
      throw new CompareEvidenceError('좌석 근거를 불러오지 못했어요.', errorCode(value));
    }
    const rows = record(value)?.rows;
    if (!Array.isArray(rows) || rows.length !== candidates.length || !rows.every(isRemoteEvidenceRow)) {
      throw new CompareEvidenceError('좌석 근거 응답을 확인하지 못했어요.');
    }
    return rows;
  }

  return Promise.all(candidates.map(async (candidate) => {
    const response = await fetcher(
      `/api/seats/${encodeURIComponent(candidateSeatKey(candidate))}/evidence${candidate.instanceId ? `?instanceId=${encodeURIComponent(candidate.instanceId)}` : ''}`,
      { signal },
    );
    const value = await readJson(response);
    if (!response.ok) throw new CompareEvidenceError('좌석 근거를 불러오지 못했어요.', errorCode(value));
    if (!isEvidence(value)) throw new CompareEvidenceError('좌석 근거 응답을 확인하지 못했어요.');
    return { evidence: value };
  }));
}

export async function loadSeatEvidence(
  candidate: SeatCandidate,
  signal: AbortSignal,
  fetcher: typeof fetch = fetch,
): Promise<SeatEvidence> {
  const response = await fetcher(
    `/api/seats/${encodeURIComponent(candidateSeatKey(candidate))}/evidence${candidate.instanceId ? `?instanceId=${encodeURIComponent(candidate.instanceId)}` : ''}`,
    { signal },
  );
  const value = await readJson(response);
  if (!response.ok) throw new CompareEvidenceError('좌석 근거를 불러오지 못했어요.', errorCode(value));
  const evidence = record(value);
  const otherConfiguration = record(evidence?.otherConfiguration);
  const distribution = evidence?.priceDistribution;
  const price = record(distribution);
  if (!isEvidence(value)
    || !Array.isArray(evidence?.evidence)
    || !evidence.evidence.every((entry: unknown) => record(entry))
    || !isCount(otherConfiguration?.count)
    || (distribution !== null && (!price || !isCount(price.n)
      || ![price.median, price.q1, price.q3].every((amount) => typeof amount === 'number' && Number.isFinite(amount))))) {
    throw new CompareEvidenceError('좌석 근거 응답을 확인하지 못했어요.');
  }
  return value as SeatEvidence;
}

async function readJson(response: Response): Promise<unknown> {
  try {
    return await response.json();
  } catch {
    throw new CompareEvidenceError('좌석 근거 응답을 읽지 못했어요.');
  }
}

function errorCode(value: unknown): string | undefined {
  const error = record(record(value)?.error);
  return typeof error?.code === 'string' ? error.code : undefined;
}

function isRemoteEvidenceRow(value: unknown): value is RemoteEvidenceRow {
  return isEvidence(record(value)?.evidence);
}

function isEvidence(value: unknown): value is RemoteEvidenceRow['evidence'] {
  const evidence = record(value);
  const counts = record(evidence?.tierCounts);
  return isCount(counts?.exact) && isCount(counts?.nearby) && isCount(counts?.section);
}

function isCount(value: unknown): value is number {
  return typeof value === 'number' && Number.isInteger(value) && value >= 0;
}

function record(value: unknown): Record<string, unknown> | undefined {
  return typeof value === 'object' && value !== null ? value as Record<string, unknown> : undefined;
}
